// Exact integer geometry (docs/02-Game-Design/Core Mechanics.md, Determinism).
// Positions are integers in sky units; crossing uses integer cross products
// and overlap uses squared integer distances, so browser, designer and Worker
// agree to the unit. Magnitudes stay far below 2^53.

export interface Point {
  readonly x: number;
  readonly y: number;
}

export function dist2(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/** Cross product of (a - o) and (b - o). */
export function cross(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** r is collinear with p–q; is it within the segment's bounding box? */
function onSegment(p: Point, q: Point, r: Point): boolean {
  return (
    Math.min(p.x, q.x) <= r.x && r.x <= Math.max(p.x, q.x) && Math.min(p.y, q.y) <= r.y && r.y <= Math.max(p.y, q.y)
  );
}

/**
 * Do segments p1–p2 and q1–q2 share any point? Touching at an interior point
 * counts; collinear overlap counts. Segments that share an endpoint star never
 * cross: the caller excludes those by node id before calling this.
 */
export function segmentsCross(p1: Point, p2: Point, q1: Point, q2: Point): boolean {
  const d1 = cross(q1, q2, p1);
  const d2 = cross(q1, q2, p2);
  const d3 = cross(p1, p2, q1);
  const d4 = cross(p1, p2, q2);
  if (d1 !== 0 && d2 !== 0 && d3 !== 0 && d4 !== 0 && d1 > 0 !== d2 > 0 && d3 > 0 !== d4 > 0) return true;
  if (d1 === 0 && onSegment(q1, q2, p1)) return true;
  if (d2 === 0 && onSegment(q1, q2, p2)) return true;
  if (d3 === 0 && onSegment(p1, p2, q1)) return true;
  if (d4 === 0 && onSegment(p1, p2, q2)) return true;
  return false;
}

/**
 * Is the distance from p to segment a–b strictly less than r? Exact: the
 * perpendicular case compares cross² with r²·|ab|² in integers.
 */
export function pointWithinOfSegment(p: Point, a: Point, b: Point, r: number): boolean {
  const r2 = r * r;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const den = dx * dx + dy * dy;
  if (den === 0) return dist2(p, a) < r2;
  const wx = p.x - a.x;
  const wy = p.y - a.y;
  const t = wx * dx + wy * dy;
  if (t <= 0) return dist2(p, a) < r2;
  if (t >= den) return dist2(p, b) < r2;
  const c = dx * wy - dy * wx;
  return c * c < r2 * den;
}
